import { CSSProperties, useCallback, useMemo, useState } from 'react'
import { Transforms } from 'slate'
import { ReactEditor, RenderElementProps, useSlateStatic } from 'slate-react'

import { ModalDialog } from 'core-components/library/modalDialog'
import { DEFAULT_VALUE_FOR_IFRAME } from 'core-components/library/richTextEditor/constants'
import { updateVideo } from 'core-components/library/richTextEditor/helpers/slateHelper'
import { getVideoInfo } from 'core-components/library/richTextEditor/helpers/videoHelper'
import {
  TVideoElement,
  VIDEO_TYPES,
} from 'core-components/library/richTextEditor/interface/custom-types'
import { TVideo } from 'core-components/library/richTextEditor/interface/TVideo'
import { VideoSettingsDialog } from 'core-components/library/richTextEditor/toolbar/components/video/VideoSettingsDialog'

import { TopPanelButtonsWrapper } from '../topPanelButtonsWrapper/TopPanelButtonsWrapper'

import styles from './Video.module.scss'

interface IProps {
  style: CSSProperties
}

export const Video = (props: RenderElementProps & IProps) => {
  const [videoSettingsDialogVisible, setVideoSettingsDialogVisible] = useState<boolean>(false)
  const element = useMemo(() => props.element as TVideoElement, [props.element])
  const editor = useSlateStatic()
  const path = ReactEditor.findPath(editor, element)

  const onDeleteVideo = useCallback(() => {
    Transforms.removeNodes(editor, { at: path })
    if (editor.onFileRemove && element.url) {
      editor.onFileRemove(element.url)
    }
  }, [path, editor, element.url])

  const onOpenSettings = useCallback(() => {
    setVideoSettingsDialogVisible(true)
  }, [])

  const onCloseVideoSettingsModal = useCallback(() => {
    setVideoSettingsDialogVisible(false)
  }, [])

  const changeVideoSettings = useCallback(
    (settings: TVideo) => {
      updateVideo(editor, path, settings)
      onCloseVideoSettingsModal()
    },
    [editor, path, onCloseVideoSettingsModal]
  )

  const videoContent = useMemo(() => {
    if (!element.url) {
      return null
    }
    const { videoType, videoUrl } = getVideoInfo({
      url: element.url,
    })
    if (videoType !== VIDEO_TYPES.default) {
      return (
        <iframe
          src={videoUrl}
          height={props.element['height'] || DEFAULT_VALUE_FOR_IFRAME}
          className={styles.root}
          contentEditable={false}
        >
          {props.children}
        </iframe>
      )
    }
    return (
      <video src={videoUrl} height={element.height} controls={true} className={styles.root}>
        {props.children}
      </video>
    )
  }, [element.height, element.url, props.children, props.element])

  return editor.disabled ? (
    videoContent
  ) : (
    <div>
      <ModalDialog
        title={'Video Settings'}
        content={VideoSettingsDialog}
        contentProps={{
          videoSettings: element,
          onSave: (settings: TVideo) => {
            changeVideoSettings(settings)
          },
          onCancel: onCloseVideoSettingsModal,
        }}
        size={'auto'}
        onClose={onCloseVideoSettingsModal}
        show={videoSettingsDialogVisible}
      />
      <TopPanelButtonsWrapper onDelete={onDeleteVideo} onOpenSettings={onOpenSettings}>
        {videoContent}
      </TopPanelButtonsWrapper>
    </div>
  )
}
